import * as React from 'react'
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    arrow,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    useMergeRefs,
    FloatingPortal,
    FloatingArrow
} from '@floating-ui/react'
import type { Placement } from '@floating-ui/react'
import classNames from 'classnames'

interface TooltipOptions {
    initialOpen?: boolean
    placement?: Placement
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

export function useTooltip({
    initialOpen = false,
    placement = 'top',
    open: controlledOpen,
    onOpenChange: setControlledOpen
}: TooltipOptions = {}) {
    const arrowRef = React.useRef(null)
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen)

    const open = controlledOpen ?? uncontrolledOpen
    const setOpen = setControlledOpen ?? setUncontrolledOpen

    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            flip({
                crossAxis: placement.includes('-'),
                fallbackAxisSideDirection: 'start',
                padding: 5
            }),
            shift({ padding: 5 }),
            arrow({ element: arrowRef })
        ]
    })

    const context = data.context

    const hover = useHover(context, {
        move: false,
        enabled: controlledOpen == null
    })
    const focus = useFocus(context, {
        enabled: controlledOpen == null
    })
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'tooltip' })

    const interactions = useInteractions([hover, focus, dismiss, role])

    return React.useMemo(
        () => ({
            open,
            setOpen,
            arrowRef,
            ...interactions,
            ...data
        }),
        [open, setOpen, arrowRef, interactions, data]
    )
}

type ContextType = ReturnType<typeof useTooltip> | null

const TooltipContext = React.createContext<ContextType>(null)

export const useTooltipContext = () => {
    const context = React.useContext(TooltipContext)

    if (context == null) {
        throw new Error('Tooltip components must be wrapped in <Tooltip />')
    }

    return context
}

export function Tooltip({
    children,
    ...options
}: { children: React.ReactNode } & TooltipOptions) {
    // This can accept any props as options, e.g. `placement`,
    // or other positioning options.
    const tooltip = useTooltip(options)
    return (
        <TooltipContext.Provider value={tooltip}>
            {children}
        </TooltipContext.Provider>
    )
}

export const TooltipTrigger = React.forwardRef<
    HTMLElement,
  React.HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger({ children, asChild = false, className, ...props }, propRef) {
    const context = useTooltipContext()
    const childrenRef = (children as any).ref
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef])

    // `asChild` allows the user to pass any element as the anchor
    if (asChild && React.isValidElement(children)) {
        const childProps = children.props as Record<string, any>
        return React.cloneElement(
            children as React.ReactElement<any>,
            context.getReferenceProps({
                ref,
                ...props,
                ...childProps,
                className: classNames('floating-tooltip-trigger', childProps.className),
                'data-state': context.open ? 'open' : 'closed'
            } as React.HTMLProps<Element>) as any
        )
    }

    return (
        <button
            ref={ref}
            className={classNames('floating-tooltip-trigger', className)}
            // The user can style the trigger based on the state
            data-state={context.open ? 'open' : 'closed'}
            {...context.getReferenceProps(props)}
        >
            {children}
        </button>
    )
})

export const TooltipContent = React.forwardRef<
    HTMLDivElement,
  React.HTMLProps<HTMLDivElement> & {
      // Arrow customization props
      arrowProps?: React.ComponentProps<typeof FloatingArrow>
  }
>(function TooltipContent({ style, className, arrowProps, ...props }, propRef) {
    const context = useTooltipContext()
    const ref = useMergeRefs([context.refs.setFloating, propRef])

    if (!context.open) return null

    return (
        <FloatingPortal>
            <div
                ref={ref}
                className={classNames('floating-tooltip-content', className)}
                style={{
                    ...context.floatingStyles,
                    ...style
                }}
                {...context.getFloatingProps(props)}
            >
                {props.children}
                <FloatingArrow
                    ref={context.arrowRef}
                    context={context.context}
                    fill="var(--bs-tertiary-bg)"
                    {...arrowProps}
                />
            </div>
        </FloatingPortal>
    )
})
